import { DateTime } from "luxon";
import type { CustomerDetailFragment } from "@/gql/graphql";
import type { StandardError } from "@/lib/error/error-utils";
import { timezoneUtils } from "@/lib/timezone";

export type { CustomerDetailFragment };
export type CustomerTier = string;

export const getCustomerPointsToNextTier = (customer: CustomerDetailFragment): number => {
  const tierMaxPoints = customer.currentTier?.maxPoints || 500;
  return Math.max(0, tierMaxPoints - customer.totalPoints);
};

export const getCustomerJoinDate = (customer: CustomerDetailFragment): DateTime => {
  return timezoneUtils.fromISO(customer.createdAt);
};

export const getCustomerLastVisit = (customer: CustomerDetailFragment): DateTime | null => {
  return customer.lastVisitDate ? timezoneUtils.fromISO(customer.lastVisitDate) : null;
};

// Filter functions
export const filterCustomersBySearch = (
  customers: CustomerDetailFragment[],
  searchTerm: string
) => {
  if (!searchTerm.trim()) return customers;

  const term = searchTerm.toLowerCase().trim();
  return customers.filter((customer) => {
    const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
    const email = customer.email?.toLowerCase() || "";

    // Search through all phone numbers
    const phoneMatches =
      customer.phoneNumbers?.some((phoneNumber) =>
        phoneNumber.phone?.toLowerCase().includes(term)
      ) || customer.primaryPhone?.phone?.toLowerCase().includes(term);

    return fullName.includes(term) || email.includes(term) || phoneMatches;
  });
};

export const filterCustomersByTier = (
  customers: CustomerDetailFragment[],
  activeTierFilters: Set<string>
) => {
  // If no tier filters are active, show all customers
  if (activeTierFilters.size === 0) {
    return customers;
  }

  // Only show customers that match the active tier filters
  return customers.filter(
    (customer) => customer.currentTierId && activeTierFilters.has(customer.currentTierId)
  );
};

// Sort functions
export const sortCustomersByName = (customers: CustomerDetailFragment[]) => {
  return [...customers].sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

export const sortCustomersByTotalSpent = (customers: CustomerDetailFragment[]) => {
  return [...customers].sort((a, b) => b.totalSpent - a.totalSpent);
};

export const sortCustomersByTotalVisits = (customers: CustomerDetailFragment[]) => {
  return [...customers].sort((a, b) => b.totalVisits - a.totalVisits);
};

export const sortCustomersByJoinDate = (customers: CustomerDetailFragment[]) => {
  return [...customers].sort((a, b) => {
    const joinDateA = getCustomerJoinDate(a);
    const joinDateB = getCustomerJoinDate(b);
    return joinDateB.toMillis() - joinDateA.toMillis();
  });
};

// Display utility functions
export const getCustomerDisplayName = (customer: CustomerDetailFragment) => {
  return `${customer.firstName} ${customer.lastName}`;
};

export const getCustomerInitials = (customer: CustomerDetailFragment) => {
  return `${customer.firstName.charAt(0)}${customer.lastName.charAt(0)}`;
};

// Tier colors

export const getTierColor = (tier: string | undefined): string => {
  if (!tier) return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300";

  switch (tier.toLowerCase()) {
    case "platinum":
      return "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100";
    case "gold":
      return "bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-300";
    case "silver":
      return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
    default:
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300";
  }
};

// Currency formatting - Convert cents to currency amount using salon currency
export const formatCurrency = (amountInCents: number, currency?: string): string => {
  const amount = amountInCents / 100;
  const currencyCode = currency || "USD"; // Fallback to USD if no currency provided

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(amount);
};

// Error handling
export const getUserFriendlyErrorMessage = (error: StandardError): string => {
  switch (error.type) {
    case "NETWORK_ERROR":
      return "Unable to load customers. Please check your connection and try again.";
    case "AUTHORIZATION_ERROR":
      return "You need permission to view customers. Please contact your administrator.";
    case "VALIDATION_ERROR":
      return "Invalid customer data. Please check your input and try again.";
    case "BUSINESS_ERROR":
      return error.message;
    default:
      return "Something went wrong loading customers. Please try again or contact support.";
  }
};

// Filter helpers
export const hasActiveCustomerFilters = (
  searchTerm: string,
  activeTierFilters: Set<string>
): boolean => {
  const hasSearchTerm = searchTerm.trim() !== "";
  const hasTierFilters = activeTierFilters.size > 0;

  return hasSearchTerm || hasTierFilters;
};
// Phone utility functions
export const getCustomerPrimaryPhone = (customer: CustomerDetailFragment): string | null => {
  // First try the primaryPhone field
  if (customer.primaryPhone?.phone) {
    return customer.primaryPhone.phone;
  }

  // Fallback to the first active primary phone number
  const primaryPhone = customer.phoneNumbers?.find((phone) => phone.isPrimary && phone.isActive);

  if (primaryPhone?.phone) {
    return primaryPhone.phone;
  }

  // Fallback to the first active phone number
  const firstActivePhone = customer.phoneNumbers?.find((phone) => phone.isActive);
  return firstActivePhone?.phone || null;
};

export const getCustomerPhoneDisplay = (customer: CustomerDetailFragment): string => {
  const phone = getCustomerPrimaryPhone(customer);
  return phone || "";
};
